import { flow, Instance, types } from 'mobx-state-tree'
import { addNetbanking } from '../../network/addNetbanking'
import { deleteNetbanking } from '../../network/deleteNetbanking'
import { getNetbankingByEntity } from '../../network/getNetbankingByEntity'
import { getNetbankingById } from '../../network/getNetbankingById'
import { netBankingProcessApprovals } from '../../network/netBankingProcessApprovals'
import { updateNetbanking } from '../../network/updateNetbanking'
import { getErrorMessage, MessageTypes } from '../../utils'
import { FORM_MESSAGE } from '../../utils/constants'
import { RootStore } from '../store'

const NetBank = types.model('NetBank', {
  type: types.optional(types.literal('netbank'), 'netbank'),
  id: types.optional(types.string, ''),
  entityId: types.maybeNull(types.number),
  entityCode: types.optional(types.string, ''),
  rpslCode: types.optional(types.string, ''),
  bankName: types.optional(types.string, ''),
  status: types.optional(types.boolean, false),
})

const Netbanking = types
  .model('Netbanking', {
    bank: types.optional(NetBank, {}),
    banks: types.array(NetBank),
  })
  .actions((self) => ({
    getById: flow(function* getById(id: number) {
      try {
        const response = yield getNetbankingById(id, RootStore.session.getTokens)
        const own = self
        own.bank = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    getByEntityId: flow(function* getByEntityId(id: number, pageInfo: any) {
      try {
        const response = yield getNetbankingByEntity(id, pageInfo, RootStore.session.getTokens)

        response?.length === 0 &&
          RootStore.message.setMessage(`No Search Result Found For Entity Id ${id}.`, MessageTypes.ERROR)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    addNB: flow(function* addNB(data: any) {
      try {
        const response = yield addNetbanking(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? '', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    deleteNB: flow(function* deleteNB(entityId, id) {
      try {
        const response = yield deleteNetbanking(entityId, id, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? '', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),

    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield netBankingProcessApprovals(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    updateNB: flow(function* updateNB(data: any) {
      try {
        const response = yield updateNetbanking(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),
  }))
  .views((self) => ({
    getNetbankingData() {
      const data = self.banks.map((item) => {
        return {
          id: item?.id,
          entityID: item?.entityId,
          bankName: item?.bankName,
          entityCode: '' + item?.entityCode,
          rpslCode: item?.rpslCode,
          status: item?.status ? 'Enabled' : 'Disabled',
        }
      })
      return data
    },
  }))

const netBankingData = Netbanking.create({
  bank: {},
  banks: [],
})

export type NetbankingType = Instance<typeof Netbanking>
export { NetBank, Netbanking, netBankingData }
